import http from '@/utils/http';

/**
 * User-related API calls
 */
export const userApi = {
  /**
   * 查找用户（分页）
   * @param {object} params - Query parameters
   * @param {string} params.email - 邮箱（可选）
   * @param {string} params.type - 用户类型 ADMINISTRATOR 或 USER（可选）
   * @param {number} params.pageNumber - 分页页码（从0开始，可选）
   * @param {number} params.pageSize - 分页大小（可选，默认20）
   * @returns {Promise}
   */
  findUsers(params) {
    return http.get('/iam/tenant-api/v1/users', { params });
  },

  /**
   * 获取指定用户
   * @param {string} id - 用户标识
   * @returns {Promise}
   */
  getUser(id) {
    return http.get(`/iam/tenant-api/v1/users/${id}`);
  },

  /**
   * 创建用户
   * @param {object} userData - 用户创建数据
   * @param {string} userData.email - 邮箱（必需）
   * @param {string} userData.password - 密码（必需）
   * @param {string} userData.sex - 性别：MALE、FEMALE、UNKNOWN（必需）
   * @param {boolean} userData.enabled - 用户启用标识（必需）
   * @param {object} userData.fullName - 姓名（可选）
   * @param {string} userData.fullName.firstName - 名
   * @param {string} userData.fullName.lastName - 姓
   * @param {string} userData.birthday - 生日（可选）
   * @param {string} userData.phoneNumber - 电话号码（可选）
   * @param {object} userData.address - 地址（可选）
   * @param {string} userData.nationality - 国籍（可选）
   * @param {object} userData.preference - 偏好设置（可选）
   * @returns {Promise}
   */
  createUser(userData) {
    return http.post('/iam/tenant-api/v1/users', userData);
  },

  /**
   * 删除用户
   * @param {string} id - 用户标识
   * @returns {Promise}
   */
  deleteUser(id) {
    return http.delete(`/iam/tenant-api/v1/users/${id}`);
  },

  /**
   * 更新用户邮箱
   * @param {string} id - 用户标识
   * @param {object} emailData - { email: '...' }
   * @returns {Promise}
   */
  updateEmail(id, emailData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/email`, emailData);
  },

  /**
   * 更新性别
   * @param {string} id - 用户标识
   * @param {object} sexData - { sex: 'MALE' | 'FEMALE' | 'UNKNOWN' }
   * @returns {Promise}
   */
  updateSex(id, sexData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/sex`, sexData);
  },

  /**
   * 更新生日
   * @param {string} id - 用户标识
   * @param {object} birthdayData - { birthday: '...' }
   * @returns {Promise}
   */
  updateBirthday(id, birthdayData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/birthday`, birthdayData);
  },

  /**
   * 更新电话号码
   * @param {string} id - 用户标识
   * @param {object} phoneData - { phoneNumber: '...' }
   * @returns {Promise}
   */
  updatePhoneNumber(id, phoneData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/telephone-number`, phoneData);
  },

  /**
   * 更新地址
   * @param {string} id - 用户标识
   * @param {object} addressData - 地址对象
   * @returns {Promise}
   */
  updateAddress(id, addressData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/address`, addressData);
  },

  /**
   * 更新偏好设置
   * @param {string} id - 用户标识
   * @param {object} preferenceData - 偏好设置
   * @returns {Promise}
   */
  updatePreference(id, preferenceData) {
    return http.put(`/iam/tenant-api/v1/users/${id}/preference`, preferenceData);
  },

  /**
   * 更新密码
   * @param {string} id - 用户标识
   * @param {object} passwordData - { currentPassword: '...', newPassword: '...' }
   * @returns {Promise}
   */
  updatePassword(id, passwordData) {
    return http.put(`/v1/users/${id}/password`, passwordData);
  },

  /**
   * 启用用户
   * @param {string} id - 用户标识
   * @returns {Promise}
   */
  enableUser(id) {
    return http.put(`/iam/tenant-api/v1/users/${id}/enable`);
  },

  /**
   * 禁用用户
   * @param {string} id - 用户标识
   * @returns {Promise}
   */
  disableUser(id) {
    return http.put(`/iam/tenant-api/v1/users/${id}/disable`);
  },

  /**
   * 为用户分配权限
   * @param {string} userId - 用户标识
   * @param {string} permissionId - 权限标识
   * @returns {Promise}
   */
  assignPermission(userId, permissionId) {
    return http.post(`/iam/tenant-api/v1/users/${userId}/permissions/${permissionId}`);
  },

  /**
   * 移除用户的权限
   * @param {string} userId - 用户标识
   * @param {string} permissionId - 权限标识
   * @returns {Promise}
   */
  removePermission(userId, permissionId) {
    return http.delete(`/iam/tenant-api/v1/users/${userId}/permissions/${permissionId}`);
  },

  /**
   * 为用户分配权限集
   * @param {string} userId - 用户标识
   * @param {string} permissionSetId - 权限集标识
   * @returns {Promise}
   */
  assignPermissionSet(userId, permissionSetId) {
    return http.post(`/iam/tenant-api/v1/users/${userId}/permission-sets/${permissionSetId}`);
  },

  /**
   * 移除用户的权限集
   * @param {string} userId - 用户标识
   * @param {string} permissionSetId - 权限集标识
   * @returns {Promise}
   */
  removePermissionSet(userId, permissionSetId) {
    return http.delete(`/iam/tenant-api/v1/users/${userId}/permission-sets/${permissionSetId}`);
  },

  /**
   * 获取所有可用权限
   * @returns {Promise}
   */
  getPermissions() {
    return http.get('/iam/tenant-api/v1/permissions');
  },

  /**
   * 获取所有可用权限集
   * @returns {Promise}
   */
  getPermissionSets() {
    return http.get('/iam/tenant-api/v1/permission-sets');
  }
};
